import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  CatalogOffering, CatalogRunningNumberKeys, CatalogService, CatalogWarehouse, CatalogWarehouseBin, InventoryLot, InventorySerial
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-serial-detail',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="isCreateMode() ? t('catalog.tracking.serials.createTitle') : t('catalog.tracking.serials.detailTitle')" [description]="t('catalog.tracking.serials.detailDescription')">
      <ain-inventory-page-hint pageKey="serialDetail" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !serial()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.tracking.serials.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.tracking.serials.newRecord') : serial()!.serialNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.tracking.serials.createHint') : serial()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/inventory/serials']">{{ t('catalog.action.backToList') }}</a>
            @if (!isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.tracking.action.quarantine')" severity="warn" (click)="updateStatus('Quarantine')"></button>
            }
            <button pButton type="button" [label]="t('catalog.action.save')" (click)="save()"></button>
          </div>
        </section>

        <section class="form-grid">
          <label>
            <span>{{ t('catalog.tracking.serialNumber') }}</span>
            <input
              pInputText
              type="text"
              [ngModel]="form.serialNumber"
              (ngModelChange)="onSerialNumberChange($event)"
              [disabled]="!isCreateMode() || isAutoSerialNumber"
              [placeholder]="t('catalog.tracking.numberAuto')" />
            @if (isCreateMode() && isAutoSerialNumber) {
              <button type="button" class="manual-link" (click)="enableManualSerialNumber()">{{ t('catalog.tracking.enterManually') }}</button>
            }
          </label>
          <label>
            <span>{{ t('catalog.inventory.offeringId') }}</span>
            <p-select [options]="offeringOptions()" [ngModel]="form.offeringId" (ngModelChange)="onOfferingChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
          </label>
          <label>
            <span>{{ t('catalog.tracking.lotId') }}</span>
            <p-select [options]="lotOptions()" [(ngModel)]="form.lotId" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
          </label>
          <label>
            <span>{{ t('catalog.inventory.warehouseId') }}</span>
            <p-select [options]="warehouseOptions()" [ngModel]="form.warehouseId" (ngModelChange)="onWarehouseChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
          </label>
          <label>
            <span>{{ t('catalog.inventory.binId') }}</span>
            <p-select [options]="binOptions()" [(ngModel)]="form.binId" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" appendTo="body" />
          </label>
          @if (!isCreateMode()) {
            <label>
              <span>{{ t('catalog.tracking.issuedReference') }}</span>
              <input pInputText type="text" [value]="serial()!.issuedReferenceNumber || '-'" disabled />
            </label>
          }
        </section>

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .form-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); padding-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .manual-link { justify-self: start; border: none; background: none; padding: 0; color: #2563eb; cursor: pointer; font-size: 12px; text-decoration: underline; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class InventorySerialDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly serial = signal<InventorySerial | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly lots = signal<InventoryLot[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);

  isAutoSerialNumber = true;

  readonly form = { serialNumber: '', offeringId: '', lotId: '', warehouseId: '', binId: '' };

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly offeringOptions = computed(() => this.offerings().map(o => ({ label: `${o.code} - ${o.title}`, value: o.id })));
  readonly lotOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.lots().map(l => ({ label: l.lotNumber, value: l.id }))
  ]);
  readonly warehouseOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.warehouses().map(w => ({ label: `${w.code} - ${w.name}`, value: w.id }))
  ]);
  readonly binOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.bins().map(b => ({ label: `${b.code} - ${b.name}`, value: b.id }))
  ]);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows.filter(o => o.inventoryEnabled)) });
    this.service.listWarehouses().subscribe({ next: rows => this.warehouses.set(rows) });
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    if (this.isCreateMode()) {
      this.isAutoSerialNumber = true;
      this.loadSerialNumberPreview();
      this.loading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.service.getInventorySerial(id).subscribe({
      next: serial => {
        this.serial.set(serial);
        this.form.serialNumber = serial.serialNumber;
        this.form.offeringId = serial.offeringId;
        this.form.lotId = serial.lotId ?? '';
        this.form.warehouseId = serial.warehouseId ?? '';
        this.form.binId = serial.binId ?? '';
        this.loadLots(serial.offeringId);
        if (serial.warehouseId) {
          this.loadBins(serial.warehouseId);
        }
        this.loading.set(false);
      },
      error: () => {
        this.serial.set(null);
        this.error.set(this.t('catalog.tracking.serials.notFound'));
        this.loading.set(false);
      }
    });
  }

  onOfferingChange(offeringId: string): void {
    this.form.offeringId = offeringId;
    this.form.lotId = '';
    this.loadLots(offeringId);
    this.loadSerialNumberPreview();
  }

  onWarehouseChange(warehouseId: string): void {
    this.form.warehouseId = warehouseId;
    this.form.binId = '';
    this.loadBins(warehouseId);
  }

  save(): void {
    if (!this.isCreateMode()) return;

    if ((!this.isAutoSerialNumber && !this.form.serialNumber.trim()) || !this.form.offeringId) {
      this.error.set(this.t('catalog.tracking.serials.requiredFields'));
      return;
    }

    this.service.createInventorySerial({
      offeringId: this.form.offeringId,
      lotId: this.form.lotId || null,
      warehouseId: this.form.warehouseId || null,
      binId: this.form.binId || null,
      serialNumber: this.isAutoSerialNumber ? '' : this.form.serialNumber.trim()
    }).subscribe({
      next: created => void this.router.navigate(['/inventory/serials', created.id]),
      error: () => this.error.set(this.t('catalog.tracking.serials.createFailed'))
    });
  }

  updateStatus(status: string): void {
    const serial = this.serial();
    if (!serial) return;

    this.service.updateInventorySerialStatus(serial.id, status).subscribe({
      next: updated => this.serial.set(updated),
      error: () => this.error.set(this.t('catalog.tracking.serials.updateFailed'))
    });
  }

  onSerialNumberChange(value: string): void {
    this.form.serialNumber = value;
    this.isAutoSerialNumber = false;
  }

  enableManualSerialNumber(): void {
    this.isAutoSerialNumber = false;
    this.form.serialNumber = '';
  }

  private loadSerialNumberPreview(): void {
    if (!this.isCreateMode() || !this.isAutoSerialNumber) return;

    this.service.previewNextRunningNumber(
      CatalogRunningNumberKeys.inventorySerial,
      CatalogRunningNumberKeys.offeringParent,
      this.form.offeringId || undefined
    ).subscribe({
      next: preview => {
        if (preview && this.isAutoSerialNumber) {
          this.form.serialNumber = preview;
        }
      }
    });
  }

  private loadLots(offeringId: string): void {
    if (!offeringId) {
      this.lots.set([]);
      return;
    }

    this.service.listInventoryLots(offeringId).subscribe({ next: rows => this.lots.set(rows) });
  }

  private loadBins(warehouseId: string): void {
    if (!warehouseId) {
      this.bins.set([]);
      return;
    }

    this.service.listWarehouseBins(warehouseId).subscribe({ next: rows => this.bins.set(rows) });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
